import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/apiClient.ts';
import { toast } from 'react-toastify';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  width: 95%;
  max-width: 720px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  ${(props) => props.$theme === 'dark' ? css`
    background: #1f2937; /* gray-800 */
    color: #f9fafb; /* gray-50 */
    border: 1px solid #374151; /* gray-700 */
  ` : css`
    background: #ffffff;
    color: #111827; /* gray-900 */
    border: 1px solid #e5e7eb; /* gray-200 */
  `}
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.6);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Body = styled.form`
  padding: 16px 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb; /* gray-200 */
  background: transparent;
  color: inherit;
`;

const Select = styled.select`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb; /* gray-200 */
  background: transparent;
  color: inherit;
`;

const Textarea = styled.textarea`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb; /* gray-200 */
  background: transparent;
  color: inherit;
`;

const Footer = styled.div`
  padding: 16px 20px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid rgba(229, 231, 235, 0.6);
`;

const Button = styled.button`
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 500;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  ${(props) => props.$variant === 'primary' && css`
    background: #2563eb; /* blue-600 */
    color: #ffffff;
    &:hover { background: #1d4ed8; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  `}
  ${(props) => props.$variant === 'secondary' && css`
    background: transparent;
    border: 1px solid #e5e7eb; /* gray-200 */
    color: inherit;
    &:hover { background: rgba(0,0,0,0.05); }
  `}
`;

// تاريخ فقط بدون وقت → YYYY-MM-DD 00:00:00
function toBackendDateOnly(localValue) {
  if (!localValue) return '';
  const dt = new Date(localValue);
  if (isNaN(dt.getTime())) return localValue;
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const y = dt.getFullYear();
  const m = pad(dt.getMonth() + 1);
  const d = pad(dt.getDate());
  return `${y}-${m}-${d} 00:00:00`;
}

// من السيرفر → YYYY-MM-DD للـ input type="date"
function toLocalDateOnly(datetime) {
  if (!datetime) return '';
  const dt = new Date(datetime);
  if (isNaN(dt.getTime())) return '';
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const y = dt.getFullYear();
  const m = pad(dt.getMonth() + 1);
  const d = pad(dt.getDate());
  return `${y}-${m}-${d}`;
}

// تنسيق المبلغ بفواصل الآلاف
function formatWithCommas(val) {
  if (val === '' || val === null || val === undefined) return '';
  const str = String(val).replace(/,/g, '');
  const num = parseFloat(str);
  if (isNaN(num)) return str;
  const parts = num.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts[1] === '00' ? parts[0] : parts.join('.');
}

function parseFormattedNumber(str) {
  if (!str || str === '') return '';
  const cleaned = String(str).replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
}

const PaymentEditModal = ({ open, recordId, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { theme } = useTheme();
  const firstRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    actiontype: 'Pay',
    paytype: 'Agent',
    ref_id: '',
    amount: '',
    notes: '',
    tx_date_local: ''
  });

  useEffect(() => {
    if (!open || !recordId) return;
    setLoading(true);
    api.get(`/v1/payments/${recordId}`)
      .then(({ data }) => {
        const debit = Number(data.amount_debit || 0);
        const credit = Number(data.amount_credit || 0);
        const amountVal = debit > 0 ? debit : credit;
        setForm({
          actiontype: data.actiontype || 'Pay',
          paytype: data.paytype || 'Agent',
          ref_id: data.ag_id || data.su_id || data.ge_id || '',
          amount: amountVal ? String(amountVal) : '',
          notes: data.notes || '',
          tx_date_local: toLocalDateOnly(data.created_at)
        });
        setTimeout(() => firstRef.current?.focus(), 50);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || e.message || t('common.error'));
      })
      .finally(() => setLoading(false));
  }, [open, recordId, t]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recordId) return;

    if (!form.amount || Number(form.amount) <= 0) {
      toast.error(isRTL ? 'الرجاء إدخال مبلغ صالح' : 'Please enter a valid amount');
      return;
    }

    // تحقق من المرجع حسب نوع الدفع حتى لا يفشل التحقق الخلفي
    if (form.paytype === 'Agent' && !form.ref_id) {
      toast.error(isRTL ? 'الرجاء اختيار الوكيل' : 'Please select an agent');
      return;
    }
    if (form.paytype === 'Supervisor' && !form.ref_id) {
      toast.error(isRTL ? 'الرجاء اختيار المشرف' : 'Please select a supervisor');
      return;
    }
    if (form.paytype === 'General' && !form.ref_id) {
      toast.error(isRTL ? 'الرجاء اختيار بند المصاريف' : 'Please select a general expense');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        actiontype: form.actiontype,
        paytype: form.paytype,
        amount: Number(form.amount),
        notes: form.notes || '',
        tx_date: toBackendDateOnly(form.tx_date_local),
      };
      if (form.paytype === 'Agent') payload.ag_id = form.ref_id || undefined;
      if (form.paytype === 'Supervisor') payload.su_id = form.ref_id || undefined;
      if (form.paytype === 'General') payload.ge_id = form.ref_id || undefined;

      await api.put(`/v1/payments/${recordId}`, payload);
      toast.success(t('common.success'));
      onSaved?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || t('common.error');
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs).flat()?.[0];
        toast.error(first ? `${msg}: ${first}` : msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay aria-hidden={!open}>
      <Modal role="dialog" aria-modal="true" aria-labelledby="payment-edit-title" $theme={theme} dir={isRTL ? 'rtl' : 'ltr'}>
        <Header>
          <Title id="payment-edit-title">{isRTL ? 'تعديل الدفع' : 'Edit Payment'}</Title>
          <button aria-label={t('common.close')} onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 18 }}>×</button>
        </Header>
        <Body onSubmit={handleSubmit}>
          <Grid>
            <Field>
              <Label htmlFor="actiontype">Action Type | نوع العملية</Label>
              <Select id="actiontype" name="actiontype" value={form.actiontype} onChange={handleChange} ref={firstRef} disabled={loading}>
                <option value="Pay">Pay</option>
                <option value="Receive">Receive</option>
              </Select>
            </Field>
            <Field>
              <Label htmlFor="paytype">Payment Type | نوع الدفع</Label>
              <Select id="paytype" name="paytype" value={form.paytype} onChange={handleChange} disabled={loading}>
                <option value="Agent">Agent</option>
                <option value="Supervisor">Supervisor</option>
                <option value="General">General</option>
              </Select>
            </Field>
            <Field style={{ display: 'none' }}>
              <Label htmlFor="ref_id">Reference | المرجع</Label>
              <Input id="ref_id" name="ref_id" value={form.ref_id} onChange={handleChange} disabled={loading} />
            </Field>
            <Field>
              <Label htmlFor="amount">Amount | المبلغ</Label>
              <Input
                id="amount"
                name="amount"
                type="text"
                inputMode="numeric"
                value={formatWithCommas(form.amount)}
                onChange={(e) => {
                  const parsed = parseFormattedNumber(e.target.value);
                  setForm((prev) => ({ ...prev, amount: parsed === '' ? '' : parsed }));
                }}
                disabled={loading}
              />
            </Field>
            <Field>
              <Label htmlFor="tx_date_local">Date | التاريخ</Label>
              <Input id="tx_date_local" name="tx_date_local" type="date" value={form.tx_date_local} onChange={handleChange} disabled={loading} />
            </Field>
            <Field style={{ gridColumn: '1 / -1' }}>
              <Label htmlFor="notes">Notes | ملاحظات</Label>
              <Textarea id="notes" name="notes" rows={3} value={form.notes} onChange={handleChange} disabled={loading} />
            </Field>
          </Grid>
          <Footer>
            <Button type="button" onClick={onClose} aria-label={t('common.cancel')} $variant="secondary" disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" aria-label={t('common.save')} $variant="primary" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </Footer>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default PaymentEditModal;